/**
 * Demander au navigateur de NE PAS effacer nos données.
 *
 * Sans cet appel, IndexedDB est en mode « best-effort » : le navigateur
 * s'autorise à l'évincer quand l'appareil manque de place. Il ne le fait pas au
 * hasard — mais sur un téléphone d'entrée de gamme, la pression de stockage est
 * la norme, pas l'exception, et c'est exactement le public d'Ariariko.
 *
 * Tant que la synchronisation n'a pas tourné au moins une fois, il n'existe
 * AUCUN filet : les données ne vivent que là. C'est la raison d'être de ce
 * fichier, et elle disparaîtra le jour où tout le monde synchronise.
 *
 * Le navigateur accorde généralement la persistance à une PWA installée ou à un
 * site visité régulièrement, et la refuse sinon. Un refus n'est pas une erreur :
 * c'est une information à faire remonter à l'écran, pas à avaler en silence.
 */

export type StorageState = 'persisted' | 'best-effort' | 'unsupported'

/**
 * Demande la persistance si elle n'est pas déjà acquise.
 *
 * Idempotent et sans coût : `persisted()` répond depuis le navigateur, et
 * redemander une persistance déjà accordée ne déclenche rien.
 */
export async function ensurePersistentStorage(): Promise<StorageState> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return 'unsupported'

  try {
    if (await navigator.storage.persisted?.()) return 'persisted'
    return (await navigator.storage.persist()) ? 'persisted' : 'best-effort'
  } catch {
    // Certains contextes (navigation privée, iframe) lèvent au lieu de refuser.
    // Un stockage qu'on ne sait pas protéger se traite comme non protégé.
    return 'unsupported'
  }
}

/** L'état actuel, sans rien demander — pour l'afficher. */
export async function storageState(): Promise<StorageState> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persisted) return 'unsupported'
  try {
    return (await navigator.storage.persisted()) ? 'persisted' : 'best-effort'
  } catch {
    return 'unsupported'
  }
}
